import { useState } from 'react'
import { useSearchParams, Link } from 'react-router-dom'
import { Button, Card, CardContent, CardHeader, CardTitle, Input } from '../../components/ui'
import { authService } from '../../services/auth.service'

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token') ?? ''

  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [done, setDone] = useState(false)

  const missingToken = !token

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters')
      return
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match')
      return
    }
    if (!/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/.test(newPassword)) {
      setError('Password must contain an uppercase letter, a lowercase letter, a number, and a special character')
      return
    }
    setSubmitting(true)
    try {
      await authService.resetPassword(token, newPassword, confirmPassword)
      setDone(true)
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to reset the password. The link may have expired.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen bg-neutral-50 flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <Card>
          <CardHeader>
            <CardTitle>Set a new password</CardTitle>
          </CardHeader>
          <CardContent>
            {missingToken ? (
              <div className="space-y-4">
                <p className="text-sm text-red-600">
                  This reset link is missing its token. Please use the link from your email, or
                  request a new one.
                </p>
                <a href="/forgot-password" className="text-sm text-primary-600 hover:underline">
                  Request a new reset link
                </a>
              </div>
            ) : done ? (
              <div className="space-y-4">
                <p className="text-sm text-green-700">
                  Your password has been reset successfully.
                </p>
                <a href="/login" className="text-sm text-primary-600 hover:underline">
                  Continue to login
                </a>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <Input
                  label="New Password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="At least 8 characters"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                />
                <Input
                  label="Confirm New Password"
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                />
                <label className="flex items-center gap-2 text-sm text-neutral-700">
                  <input
                    type="checkbox"
                    checked={showPassword}
                    onChange={(e) => setShowPassword(e.target.checked)}
                    className="rounded border-neutral-300"
                  />
                  Show passwords
                </label>
                {error && <p className="text-sm text-red-600">{error}</p>}
                <Button type="submit" disabled={submitting} className="w-full">
                  {submitting ? 'Saving…' : 'Reset password'}
                </Button>
              </form>
            )}
            <div className="mt-4 text-center">
              <a href="/login" className="text-sm text-primary-600 hover:underline">
                ← Back to login
              </a>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
